<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useTranslation } from '@/utils/locales/messages';
import { baseCodeApi } from '@/shared/api/baseCodeApi';

const props = defineProps<{
  modelValue: string;
  label?: string;
  readonly?: boolean;
  required?: boolean;
}>();

const emit = defineEmits<{ 'update:modelValue': [value: string] }>();

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

const nationalityOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
const isLoading = ref(false);

const displayName = computed(() => {
  if (!props.modelValue) return '';
  const option = nationalityOptions.value.find((opt) => opt.TOTAL_CODE === props.modelValue);
  return option?.CODE_NAME || props.modelValue;
});

const fieldLabel = computed(
  () => props.label || t('setupEntity.sgIncorporation.fields.agentNationality') || 'Nationality',
);

async function loadNationalityOptions() {
  isLoading.value = true;
  try {
    const response = await baseCodeApi.getCodesByMainCode('CM', 'B04', undefined);
    const codes = (response as any).codes || (response as any).data?.codes || [];

    const mapped = codes
      .map((code: any) => ({
        TOTAL_CODE: String(code.totalCode || code.TOTAL_CODE || '').trim(),
        CODE_NAME: String(
          code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
        ).trim(),
      }))
      .filter((item: { TOTAL_CODE: string; CODE_NAME: string }) => item.TOTAL_CODE && item.CODE_NAME);

    const uniqueCodesMap = new Map<string, { TOTAL_CODE: string; CODE_NAME: string }>();
    mapped.forEach((item) => {
      if (!uniqueCodesMap.has(item.TOTAL_CODE)) {
        uniqueCodesMap.set(item.TOTAL_CODE, item);
      }
    });

    nationalityOptions.value = Array.from(uniqueCodesMap.values()).filter(
      (item) => item.TOTAL_CODE.slice(-3) !== '000',
    );
  } catch {
    nationalityOptions.value = [];
  } finally {
    isLoading.value = false;
  }
}

function updateValue(val: string | null) {
  emit('update:modelValue', val || '');
}

onMounted(() => {
  loadNationalityOptions();
});
</script>

<template>
  <v-text-field
    v-if="readonly"
    :model-value="displayName"
    :label="fieldLabel"
    variant="outlined"
    density="comfortable"
    hide-details
    readonly
  />
  <v-autocomplete
    v-else
    :model-value="modelValue || null"
    :items="nationalityOptions"
    item-title="CODE_NAME"
    item-value="TOTAL_CODE"
    :label="fieldLabel"
    :rules="required ? [(v: string) => !!v || t('setupEntity.sgIncorporation.validation.required')] : undefined"
    variant="outlined"
    density="comfortable"
    :placeholder="t('setupEntity.Please Select') || 'Please Select'"
    hide-details
    clearable
    :loading="isLoading"
    :disabled="isLoading"
    @update:model-value="updateValue"
  />
</template>
